import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { WS_BASE } from "../lib/api";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import ChatBox from "../components/ChatBox";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, MicOff, Video as VideoIcon, VideoOff, PhoneOff, Copy, Check, X, Flag, Ban, UserPlus, Sparkles, Loader2, Wifi, SwitchCamera, RefreshCw, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const QUALITY = {
  excellent: { label: "Excellent", color: "text-emerald-400", dot: "bg-emerald-400", bars: 4 },
  good: { label: "Good", color: "text-lime-400", dot: "bg-lime-400", bars: 3 },
  fair: { label: "Fair", color: "text-amber-400", dot: "bg-amber-400", bars: 2 },
  poor: { label: "Poor", color: "text-red-400", dot: "bg-red-400", bars: 1 },
  unknown: { label: "Connecting", color: "text-white/50", dot: "bg-white/40", bars: 0 },
};

function gradeQuality({ rtt, loss }) {
  if (rtt == null && loss == null) return "unknown";
  const ms = (rtt ?? 0) * 1000;
  const l = loss ?? 0;
  if (ms < 150 && l < 1) return "excellent";
  if (ms < 250 && l < 3) return "good";
  if (ms < 450 && l < 7) return "fair";
  return "poor";
}

function QualityBadge({ quality, stats, open, onToggle }) {
  const q = QUALITY[quality] || QUALITY.unknown;
  return (
    <div className="relative">
      <button
        onClick={onToggle}
        data-testid="call-quality-badge"
        className={`flex items-center gap-1.5 text-sm font-semibold bg-white/10 hover:bg-white/15 rounded-full px-3 py-1.5 transition-colors ${q.color}`}
        title="Connection quality — click for details"
      >
        <span className="flex items-end gap-0.5 h-3.5">
          {[1, 2, 3, 4].map((b) => (
            <span key={b} className={`w-1 rounded-sm ${b <= q.bars ? q.dot : "bg-white/15"}`} style={{ height: `${b * 3 + 2}px` }} />
          ))}
        </span>
        <span className="hidden sm:inline">{q.label}</span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
            className="absolute right-0 mt-2 w-56 rounded-2xl bg-[#1d1714] border border-white/10 shadow-2xl p-3 z-30"
            data-testid="call-quality-panel"
          >
            <div className="flex items-center gap-2 mb-2">
              <Wifi className={`w-4 h-4 ${q.color}`} />
              <span className={`font-heading font-bold ${q.color}`}>{q.label} connection</span>
            </div>
            <dl className="text-sm space-y-1.5 text-white/70">
              <div className="flex justify-between"><dt>Ping</dt><dd data-testid="stat-ping" className="font-mono text-white">{stats.rtt != null ? `${Math.round(stats.rtt * 1000)} ms` : "—"}</dd></div>
              <div className="flex justify-between"><dt>Packet loss</dt><dd data-testid="stat-loss" className="font-mono text-white">{stats.loss != null ? `${stats.loss.toFixed(1)}%` : "—"}</dd></div>
              <div className="flex justify-between"><dt>Jitter</dt><dd data-testid="stat-jitter" className="font-mono text-white">{stats.jitter != null ? `${Math.round(stats.jitter * 1000)} ms` : "—"}</dd></div>
              <div className="flex justify-between"><dt>Bitrate</dt><dd data-testid="stat-bitrate" className="font-mono text-white">{stats.kbps != null ? `${stats.kbps} kbps` : "—"}</dd></div>
              <div className="flex justify-between"><dt>Resolution</dt><dd data-testid="stat-res" className="font-mono text-white">{stats.res || "—"}</dd></div>
            </dl>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const ICE = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
    { urls: "stun:stun2.l.google.com:19302" },
    { urls: "stun:stun3.l.google.com:19302" },
    { urls: "stun:stun4.l.google.com:19302" },
    { urls: "stun:stun.cloudflare.com:3478" },
  ],
  iceCandidatePoolSize: 10,
};

const MAX_VIDEO_BITRATE = 1_200_000; // ~1.2 Mbps cap keeps calls smooth on slower networks

// per-quality adaptive video: each peer sends at a scale their connection can handle
const ADAPTIVE = {
  excellent: { maxBitrate: 1_200_000, scale: 1 },
  good: { maxBitrate: 800_000, scale: 1 },
  fair: { maxBitrate: 400_000, scale: 2 },
  poor: { maxBitrate: 150_000, scale: 4 },
  unknown: { maxBitrate: 800_000, scale: 1 },
};
const POOR_HOLD_MS = 6000; // sustained "poor" before dropping to audio-only
const RECOVER_HOLD_MS = 4000; // sustained good/excellent before restoring video

function CardView({ card, accent, onRefresh, refreshing }) {
  if (!card) return <div className="h-40 rounded-2xl bg-white/10 animate-pulse" />;
  const isStarter = card.type === "starter";
  return (
    <div className={`relative rounded-2xl p-5 pb-10 ${accent ? "bg-primary text-primary-foreground" : "bg-white text-[#25201b]"}`}>
      <div className="flex items-center gap-2 mb-1">
        {isStarter && <MessageCircle className={`w-3.5 h-3.5 ${accent ? "opacity-90" : "text-primary"}`} />}
        <span className={`text-[10px] uppercase tracking-widest font-bold ${accent ? "opacity-80" : "text-black/45"}`}>
          {isStarter ? "Conversation Starter" : `${card.target_language} · ${card.tier}`}
        </span>
      </div>
      <p className={`font-heading font-black leading-tight ${isStarter ? "text-xl" : "text-2xl"}`}>{card.front}</p>
      {card.pronunciation && <p className={`text-sm mt-0.5 ${accent ? "opacity-80" : "text-black/45"}`}>/{card.pronunciation}/</p>}
      <p className={`text-sm mt-2 ${accent ? "opacity-90" : "text-black/70"}`}>{isStarter ? "→ " : "= "}{card.back}</p>
      {card.example && <p className={`text-xs italic mt-2 ${accent ? "opacity-80" : "text-black/45"}`}>“{card.example}”</p>}
      {onRefresh && (
        <button
          onClick={onRefresh}
          disabled={refreshing}
          data-testid="card-refresh-btn"
          title="Refresh — gets a little harder each time"
          className={`absolute bottom-2.5 left-2.5 flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors disabled:opacity-60 ${accent ? "bg-white/20 hover:bg-white/30 text-white" : "bg-black/5 hover:bg-black/10 text-black/60"}`}
        >
          <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </button>
      )}
    </div>
  );
}

export default function VideoRoom() {
  const { roomId } = useParams();
  const { state } = useLocation();
  const nav = useNavigate();
  const { refreshUser, user } = useAuth();
  const { lang } = useI18n();
  const partner = state?.partner;

  const localRef = useRef(null);
  const remoteRef = useRef(null);
  const pcRef = useRef(null);
  const wsRef = useRef(null);
  const streamRef = useRef(null);
  const offeredRef = useRef(false);
  const isOffererRef = useRef(false);
  const restartTimerRef = useRef(null);

  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [quality, setQuality] = useState("unknown");
  const [netStats, setNetStats] = useState({});
  const [qualityOpen, setQualityOpen] = useState(false);
  const [autoAudioOnly, setAutoAudioOnly] = useState(false);
  const [swapped, setSwapped] = useState(false);
  const [messages, setMessages] = useState([]);
  const [chatOpen, setChatOpen] = useState(true);
  const statsPrevRef = useRef(null);
  const poorSinceRef = useRef(null);
  const goodSinceRef = useRef(null);
  const camOnRef = useRef(true);
  const autoAudioOnlyRef = useRef(false);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [myCard, setMyCard] = useState(null);
  const [partnerCard, setPartnerCard] = useState(null);
  const [myId, setMyId] = useState(null);
  const [myPoints, setMyPoints] = useState(0);
  const [myTier, setMyTier] = useState("Beginner");
  const [loadingCards, setLoadingCards] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportKind, setReportKind] = useState("report");
  const [reportText, setReportText] = useState("");
  const [friendRequested, setFriendRequested] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const [pointDrop, setPointDrop] = useState(false);
  const [deductionsUsed, setDeductionsUsed] = useState(0);
  const myRefreshRef = useRef(0);
  const [refreshingMine, setRefreshingMine] = useState(false);
  const [refreshingPartner, setRefreshingPartner] = useState(false);

  const send = (msg) => wsRef.current?.readyState === 1 && wsRef.current.send(JSON.stringify(msg));

  const langRef = useRef(lang);
  useEffect(() => { langRef.current = lang; }, [lang]);

  const fetchSession = useCallback(async () => {
    if (!partner?.id) return;
    setLoadingCards(true);
    try {
      // sync current site locale so cards are generated in this language
      await api.post("/me/locale", { locale: langRef.current }).catch(() => {});
      const { data } = await api.post("/call/session", { room_id: roomId, partner_id: partner.id });
      setMyCard(data.my_card);
      setPartnerCard(data.partner_card);
      setMyId(data.my_id);
      setMyPoints(data.my_points);
      setMyTier(data.my_tier);
      setDeductionsUsed(data.my_deductions_used || 0);
    } catch (e) {
      toast.error("Could not load the call flashcards.");
    } finally {
      setLoadingCards(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run only when partner changes; roomId is stable from useParams for the call's lifetime
  }, [partner?.id]);

  useEffect(() => { fetchSession(); }, [fetchSession]);

  const myIdRef = useRef(null);
  useEffect(() => { myIdRef.current = myId; }, [myId]);

  // when the site language changes mid-call, regenerate my card in the new language
  const didMountLangRef = useRef(false);
  useEffect(() => {
    if (!didMountLangRef.current) { didMountLangRef.current = true; return; }
    if (!myId) return;
    (async () => {
      await api.post("/me/locale", { locale: lang }).catch(() => {});
      setRefreshingMine(true);
      await api.post("/call/refresh-session", { room_id: roomId, target_id: myId, bump: false }).catch(() => setRefreshingMine(false));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires only on locale change; myId/roomId are read at call time and stable
  }, [lang]);

  // in-call presence heartbeat
  useEffect(() => {
    const ping = () => api.post("/presence/heartbeat", { status: "in_call" }).catch(() => {});
    ping();
    const t = setInterval(ping, 12000);
    return () => clearInterval(t);
  }, []);

  // live call-quality sampling from real WebRTC getStats()
  useEffect(() => {
    if (!connected) { setQuality("unknown"); setNetStats({}); statsPrevRef.current = null; poorSinceRef.current = null; goodSinceRef.current = null; setAutoAudioOnly(false); return; }
    const sample = async () => {
      const pc = pcRef.current;
      if (!pc) return;
      let rtt, jitter, res, lost, recv, bytes, ts;
      try {
        const report = await pc.getStats();
        report.forEach((r) => {
          if (r.type === "inbound-rtp" && (r.kind === "video" || r.mediaType === "video")) {
            jitter = r.jitter;
            lost = r.packetsLost;
            recv = r.packetsReceived;
            bytes = r.bytesReceived;
            ts = r.timestamp;
            if (r.frameWidth && r.frameHeight) res = `${r.frameWidth}×${r.frameHeight}`;
          }
          if (r.type === "candidate-pair" && r.nominated && r.currentRoundTripTime != null) {
            rtt = r.currentRoundTripTime;
          }
        });
      } catch (e) { return; }

      let loss = null, kbps = null;
      const prev = statsPrevRef.current;
      if (prev && ts && ts > prev.ts) {
        const dLost = (lost ?? 0) - prev.lost;
        const dRecv = (recv ?? 0) - prev.recv;
        const total = dLost + dRecv;
        if (total > 0) loss = Math.max(0, (dLost / total) * 100);
        const dBytes = (bytes ?? 0) - prev.bytes;
        const dSec = (ts - prev.ts) / 1000;
        if (dSec > 0) kbps = Math.round((dBytes * 8) / 1000 / dSec);
      }
      statsPrevRef.current = { lost: lost ?? 0, recv: recv ?? 0, bytes: bytes ?? 0, ts: ts ?? Date.now() };

      const next = { rtt, jitter, loss, kbps, res };
      setNetStats(next);
      const q = reconnecting ? "poor" : gradeQuality({ rtt, loss });
      setQuality(q);

      // adaptive: send video at the scale/bitrate this connection can handle
      const sender = pc.getSenders().find((s) => s.track?.kind === "video");
      if (sender) {
        const cfg = ADAPTIVE[q] || ADAPTIVE.good;
        try {
          const params = sender.getParameters();
          if (!params.encodings || !params.encodings.length) params.encodings = [{}];
          params.encodings[0].maxBitrate = cfg.maxBitrate;
          params.encodings[0].scaleResolutionDownBy = cfg.scale;
          await sender.setParameters(params);
        } catch (e) { console.debug("adaptive setParameters failed", e); }
      }

      // auto audio-only: drop video after sustained "poor", restore after sustained recovery
      const now = Date.now();
      if (q === "poor") {
        goodSinceRef.current = null;
        if (!poorSinceRef.current) poorSinceRef.current = now;
        if (!autoAudioOnlyRef.current && now - poorSinceRef.current > POOR_HOLD_MS) setAutoAudioOnly(true);
      } else {
        poorSinceRef.current = null;
        if (autoAudioOnlyRef.current) {
          if (q === "good" || q === "excellent") {
            if (!goodSinceRef.current) goodSinceRef.current = now;
            if (now - goodSinceRef.current > RECOVER_HOLD_MS) setAutoAudioOnly(false);
          } else {
            goodSinceRef.current = null; // 'fair' is too shaky to restore yet
          }
        }
      }
    };
    sample();
    const t = setInterval(sample, 2000);
    return () => clearInterval(t);
  }, [connected, reconnecting]);

  // keep refs in sync + apply outgoing video enabled state (user intent AND auto mode)
  useEffect(() => { camOnRef.current = camOn; autoAudioOnlyRef.current = autoAudioOnly;
    const track = streamRef.current?.getVideoTracks()[0];
    if (track) track.enabled = camOn && !autoAudioOnly;
  }, [camOn, autoAudioOnly]);


  useEffect(() => {
    let mounted = true;

    const makeOffer = async (iceRestart = false) => {
      if (!pcRef.current) return;
      if (!iceRestart && offeredRef.current) return;
      offeredRef.current = true;
      isOffererRef.current = true;
      const offer = await pcRef.current.createOffer(iceRestart ? { iceRestart: true } : {});
      await pcRef.current.setLocalDescription(offer);
      send({ type: "offer", sdp: offer });
    };

    const capBitrate = async () => {
      const sender = pcRef.current?.getSenders().find((s) => s.track?.kind === "video");
      if (!sender) return;
      try {
        const params = sender.getParameters();
        if (!params.encodings || !params.encodings.length) params.encodings = [{}];
        params.encodings[0].maxBitrate = MAX_VIDEO_BITRATE;
        await sender.setParameters(params);
      } catch (e) { console.debug("bitrate cap failed", e); }
    };

    const scheduleRestart = () => {
      if (restartTimerRef.current) return;
      setReconnecting(true);
      restartTimerRef.current = setTimeout(() => {
        restartTimerRef.current = null;
        const st = pcRef.current?.iceConnectionState;
        if (st === "disconnected" || st === "failed") {
          // only the offerer renegotiates to avoid glare; answerer waits for the fresh offer
          if (isOffererRef.current) makeOffer(true).catch(() => {});
        }
      }, 2000);
    };

    const clearRestart = () => {
      if (restartTimerRef.current) { clearTimeout(restartTimerRef.current); restartTimerRef.current = null; }
      setReconnecting(false);
    };

    const initiateCallSession = async () => {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      } catch (e) {
        toast.error("Camera/mic access is required for the call.");
        return;
      }
      if (!mounted) { stream.getTracks().forEach((t) => t.stop()); return; }
      streamRef.current = stream;
      if (localRef.current) localRef.current.srcObject = stream;

      const pc = new RTCPeerConnection(ICE);
      pcRef.current = pc;
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      await capBitrate();
      pc.ontrack = (e) => { if (remoteRef.current) remoteRef.current.srcObject = e.streams[0]; setConnected(true); };
      pc.onicecandidate = (e) => { if (e.candidate) send({ type: "candidate", candidate: e.candidate }); };
      pc.oniceconnectionstatechange = () => {
        const st = pc.iceConnectionState;
        if (st === "connected" || st === "completed") { setConnected(true); clearRestart(); }
        else if (st === "disconnected" || st === "failed") { scheduleRestart(); }
      };

      const ws = new WebSocket(`${WS_BASE}/api/ws/signal/${roomId}`);
      wsRef.current = ws;
      ws.onmessage = async (evt) => {
        const msg = JSON.parse(evt.data);
        if (msg.type === "peers") {
          if (msg.count >= 2) makeOffer();
        } else if (msg.type === "offer") {
          await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          send({ type: "answer", sdp: answer });
        } else if (msg.type === "answer") {
          await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
        } else if (msg.type === "candidate") {
          try { await pc.addIceCandidate(new RTCIceCandidate(msg.candidate)); } catch (e) { console.debug("addIceCandidate failed", e); }
        } else if (msg.type === "peer-left") {
          setConnected(false);
          if (remoteRef.current) remoteRef.current.srcObject = null;
        } else if (msg.type === "chat") {
          setMessages((prev) => [...prev, msg.msg]);
        } else if (msg.type === "session-push") {
          // backend pushed a card/points update (Correct, Refresh or Incorrect) to both peers instantly
          if (msg.deducted) {
            if (msg.target_id === myIdRef.current) {
              setMyPoints(msg.points);
              setMyTier(msg.tier);
              setPointDrop(true);
              setTimeout(() => setPointDrop(false), 1500);
              toast.error(`−${msg.deducted_amount || 5} points`);
              refreshUser();
            }
          } else if (msg.target_id === myIdRef.current) {
            setMyCard(msg.card);
            if (msg.refresh) {
              setRefreshingMine(false);
            } else {
              setMyPoints(msg.points);
              setMyTier(msg.tier);
              myRefreshRef.current = 0;
              setCelebrate(true);
              setTimeout(() => setCelebrate(false), 1800);
              toast.success(`🎉 +10 points! ${msg.leveled_up ? `Level up → ${msg.tier}!` : ""}`);
              (msg.new_badges || []).forEach((b) => toast.success(`🏅 Achievement unlocked: ${b.label}!`));
              refreshUser();
            }
          } else {
            setPartnerCard(msg.card);
            if (msg.refresh) setRefreshingPartner(false);
          }
        }
      };
    };
    initiateCallSession();

    return () => {
      mounted = false;
      if (restartTimerRef.current) clearTimeout(restartTimerRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      pcRef.current?.close();
      wsRef.current?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run-once WebRTC/WS setup per room; refreshUser is read via closure and must not re-init the connection
  }, [roomId]);

  const toggleMic = () => { const t = streamRef.current?.getAudioTracks()[0]; if (t) { t.enabled = !t.enabled; setMicOn(t.enabled); } };
  const toggleCam = () => setCamOn((v) => !v);
  const toggleSwap = () => setSwapped((v) => !v);
  const endCall = () => nav("/app");
  const copyLink = () => { navigator.clipboard.writeText(window.location.href); toast.success("Room link copied — share it to invite someone!"); };

  const markCorrect = async () => {
    if (!partner?.id) return;
    try {
      const { data } = await api.post("/call/correct-session", { room_id: roomId, target_id: partner.id });
      // backend broadcasts 'session-push' to both peers; apply immediately here too
      setPartnerCard(data.card);
      toast.success(`You gave ${partner.name} +10 points!`);
    } catch (e) {
      toast.error("Could not award points.");
    }
  };

  const markIncorrect = async () => {
    if (!partner?.id || deductionsUsed >= 3) return;
    try {
      const { data } = await api.post("/call/incorrect-session", { room_id: roomId, target_id: partner.id });
      setDeductionsUsed(data.deductions_used);
      toast(`−5 for ${partner.name?.split(" ")[0] || "partner"} · ${data.deductions_left} deductions left`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not deduct points.");
    }
  };

  const refreshMyCard = async () => {
    if (!myId) return;
    setRefreshingMine(true);
    myRefreshRef.current += 1;
    if (myRefreshRef.current > 3) toast("Struggling with this level? Try dropping down in Challenge Mode (Profile).", { icon: "💡" });
    try {
      await api.post("/call/refresh-session", { room_id: roomId, target_id: myId });
      // backend broadcasts 'session-push' with refresh:true to both peers
    } catch (e) {
      setRefreshingMine(false);
      toast.error("Could not refresh the card.");
    }
  };

  const refreshPartnerCard = async () => {
    if (!partner?.id) return;
    setRefreshingPartner(true);
    try {
      await api.post("/call/refresh-session", { room_id: roomId, target_id: partner.id });
    } catch (e) {
      setRefreshingPartner(false);
      toast.error("Could not refresh the card.");
    }
  };

  const addFriend = async () => {
    if (!partner?.id) return;
    await api.post(`/friends/request/${partner.id}`).catch(() => {});
    setFriendRequested(true);
    toast.success(`Friend request sent to ${partner.name}`);
  };

  const sendChat = (text) => {
    const m = {
      id: `${myId || "me"}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      text,
      senderId: myId,
      senderName: user?.name || "You",
      ts: Date.now(),
    };
    send({ type: "chat", msg: m });
    setMessages((prev) => [...prev, m]);
  };

  const translateMessage = async (text) => {
    const nativeName = (user?.native_languages || ["English"])[0];
    const code = nativeName === "Spanish" ? "es" : "en";
    const { data } = await api.post("/translate", { target_lang: code, texts: { m: text } });
    return data.translations?.m || text;
  };

  const saveMessageNote = async (m, translation) => {
    let saidAt;
    try { saidAt = m.ts ? new Date(m.ts).toISOString() : new Date().toISOString(); }
    catch { saidAt = new Date().toISOString(); }
    await api.post("/study-notes", {
      original: m.text,
      translation: translation || "",
      author_name: m.senderId === myId ? (user?.name || "You") : (m.senderName || "Partner"),
      author_id: m.senderId || "",
      said_at: saidAt,
    });
    toast.success("Saved to your Study Notes");
  };

  const submitReport = async () => {
    if (!partner?.id) return;
    try {
      if (reportKind === "block") {
        await api.post(`/block/${partner.id}`, { reason: reportText, room_id: roomId, content: reportText });
        toast.success(`${partner.name} was blocked and reported to moderation.`);
        setReportOpen(false);
        endCall();
      } else {
        await api.post("/report", { reported_user_id: partner.id, reason: reportText, content: reportText, room_id: roomId });
        toast.success("Report sent to the moderation team.");
        setReportOpen(false);
      }
      setReportText("");
    } catch (e) {
      toast.error("Could not submit. Please try again.");
    }
  };

  return (
    <div className="h-screen bg-[#141010] text-white flex flex-col overflow-hidden">
      <div className="px-5 sm:px-8 h-16 shrink-0 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-3">
          <span className="w-2.5 h-2.5 rounded-full bg-secondary animate-pulse" />
          <span className="font-heading font-bold">Room {roomId}</span>
          {partner && <span className="text-white/60 text-sm hidden sm:inline">with {partner.name}</span>}
        </div>
        <div className="flex items-center gap-2">
          <QualityBadge quality={quality} stats={netStats} open={qualityOpen} onToggle={() => setQualityOpen((v) => !v)} />
          <span className="flex items-center gap-1.5 text-sm font-semibold bg-secondary/20 text-secondary rounded-full px-3 py-1.5" data-testid="room-my-points">
            <Sparkles className="w-3.5 h-3.5" /> {myPoints} pts · {myTier}
          </span>
          <Button variant="ghost" size="sm" className="rounded-full text-white hover:bg-white/10" onClick={copyLink} data-testid="copy-room-link">
            <Copy className="w-4 h-4 mr-1" /> Invite
          </Button>
          <LanguageSwitcher />
        </div>
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-3 gap-4 p-3 sm:p-4 overflow-hidden">
        {/* video */}
        <div className="lg:col-span-2 relative rounded-3xl overflow-hidden bg-black/40 border border-white/10 min-h-0">
          {(() => { const big = "absolute inset-0 w-full h-full object-cover"; const pip = "absolute bottom-4 right-4 w-36 sm:w-48 aspect-video object-cover rounded-2xl border-2 border-white/30 shadow-xl bg-black z-20 cursor-pointer hover:border-secondary transition-colors"; return (
          <>
          <video ref={remoteRef} autoPlay playsInline muted={false}
            onDoubleClick={swapped ? toggleSwap : undefined}
            title={swapped ? "Double-click to swap back" : undefined}
            className={swapped ? pip : big} data-testid="remote-video" />
          <video ref={localRef} autoPlay playsInline muted
            onDoubleClick={swapped ? undefined : toggleSwap}
            title={swapped ? undefined : "Double-click to swap"}
            className={swapped ? big : pip} data-testid="local-video" />
          </>
          ); })()}
          {/* swap hint on the small cam (bottom-right) */}
          <div className="absolute bottom-2 right-2 z-30 pointer-events-none flex items-center gap-1 bg-black/55 backdrop-blur rounded-full px-2 py-0.5 text-[10px] text-white/85" data-testid="swap-hint">
            <SwitchCamera className="w-3 h-3" /> Double-click to swap
          </div>
          {connected && reconnecting && (
            <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/60 backdrop-blur rounded-full px-3 py-1.5 text-sm text-amber-300 z-10" data-testid="reconnecting-indicator">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Reconnecting…
            </div>
          )}
          {connected && autoAudioOnly && !reconnecting && (
            <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/60 backdrop-blur rounded-full px-3 py-1.5 text-sm text-amber-300 z-10" data-testid="audio-only-indicator">
              <VideoOff className="w-3.5 h-3.5" /> Audio-only · saving your connection
            </div>
          )}
          {!connected && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6" data-testid="waiting-peer">
              <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mb-4"><VideoIcon className="w-7 h-7" /></div>
              <p className="font-heading font-bold text-xl">Waiting for {partner?.name || "your partner"} to join…</p>
              <p className="text-white/60 mt-1 text-sm">Practice your flashcard while you wait.</p>
            </div>
          )}
          <AnimatePresence>
            {celebrate && (
              <motion.div initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ opacity: 0 }}
                className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="text-6xl font-heading font-black text-secondary drop-shadow-lg">+10 🎉</span>
              </motion.div>
            )}
            {pointDrop && (
              <motion.div initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ opacity: 0 }}
                className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none" data-testid="point-deducted-anim">
                <span className="text-6xl font-heading font-black text-red-500 drop-shadow-lg">−5</span>
                <span className="mt-1 text-sm font-bold text-red-300">Point Deducted</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* right column: flashcards + collapsible chat */}
        <div className="flex flex-col gap-3 min-h-0">
        {/* flashcard panel */}
        <div className="rounded-3xl bg-white/5 border border-white/10 p-4 flex flex-col gap-4 overflow-auto min-h-0 flex-1" data-testid="flashcard-panel">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-bold flex items-center gap-2"><Sparkles className="w-4 h-4 text-secondary" /> Live Flashcards</h3>
            {loadingCards && <Loader2 className="w-4 h-4 animate-spin text-white/60" data-testid="cards-loading" />}
          </div>

          <div>
            <p className="text-xs uppercase tracking-widest font-bold text-white/50 mb-2">Your card — say it out loud</p>
            <CardView card={myCard} accent onRefresh={refreshMyCard} refreshing={refreshingMine} />
          </div>

          <div>
            <p className="text-xs uppercase tracking-widest font-bold text-white/50 mb-2">{partner?.name || "Partner"}'s card — help & correct</p>
            <CardView card={partnerCard} onRefresh={refreshPartnerCard} refreshing={refreshingPartner} />
            <div className="flex gap-2 mt-3">
              <Button onClick={markCorrect} disabled={!partner?.id} className="flex-1 rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground" data-testid="correct-btn">
                <Check className="w-4 h-4 mr-1" /> Correct! (+10)
              </Button>
              <Button onClick={markIncorrect} disabled={!partner?.id || deductionsUsed >= 3} title={deductionsUsed >= 3 ? "No deductions left this session" : "Deduct 5 points"} className="rounded-full bg-destructive/90 hover:bg-destructive text-white" data-testid="incorrect-btn">
                <X className="w-4 h-4 mr-1" /> −5 <span className="opacity-70 ml-1">({3 - deductionsUsed})</span>
              </Button>
            </div>
          </div>

          <div className="mt-auto grid grid-cols-3 gap-2 pt-2">
            <Button variant="outline" className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10" onClick={addFriend} disabled={friendRequested || !partner?.id} data-testid="room-add-friend">
              <UserPlus className="w-4 h-4" />
            </Button>
            <Button variant="outline" className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10" onClick={() => { setReportKind("report"); setReportOpen(true); }} disabled={!partner?.id} data-testid="room-report">
              <Flag className="w-4 h-4" />
            </Button>
            <Button variant="outline" className="rounded-full border-destructive/40 bg-transparent text-destructive hover:bg-destructive/10" onClick={() => { setReportKind("block"); setReportOpen(true); }} disabled={!partner?.id} data-testid="room-block">
              <Ban className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <ChatBox
          messages={messages}
          myId={myId}
          onSend={sendChat}
          translate={translateMessage}
          saveNote={saveMessageNote}
          open={chatOpen}
          onToggle={() => setChatOpen((v) => !v)}
        />
        </div>
      </div>

      <div className="px-5 sm:px-8 py-4 shrink-0 flex items-center justify-center gap-3">
        <button onClick={toggleMic} data-testid="toggle-mic" className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${micOn ? "bg-white/10 hover:bg-white/20" : "bg-destructive"}`}>
          {micOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
        </button>
        <button onClick={toggleCam} data-testid="toggle-cam" className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${camOn ? "bg-white/10 hover:bg-white/20" : "bg-destructive"}`}>
          {camOn ? <VideoIcon className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
        </button>
        <button onClick={endCall} data-testid="end-call" className="w-14 h-12 rounded-full bg-destructive hover:brightness-110 flex items-center justify-center">
          <PhoneOff className="w-5 h-5" />
        </button>
      </div>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="rounded-3xl" data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{reportKind === "block" ? "Block & report" : "Report"} {partner?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-foreground/70 -mt-2">Describe what happened. This is sent to our moderation team for review.</p>
          <Textarea value={reportText} onChange={(e) => setReportText(e.target.value)} rows={4} placeholder="What did they say or do?"
            className="rounded-2xl bg-accent/50 border-transparent" data-testid="report-textarea" />
          <DialogFooter>
            <Button variant="ghost" className="rounded-full" onClick={() => setReportOpen(false)} data-testid="report-cancel">Cancel</Button>
            <Button className={`rounded-full ${reportKind === "block" ? "bg-destructive hover:bg-destructive/90" : ""}`} onClick={submitReport} data-testid="report-submit">
              {reportKind === "block" ? "Block & report" : "Send report"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
